import { z } from 'zod';
import { UserRefSchema } from '../user-ref';

export const POLL_STATUSES = ['draft', 'open', 'closed'] as const;
export const PollStatusSchema = z.enum(POLL_STATUSES);
export type PollStatus = z.infer<typeof PollStatusSchema>;

export const POLL_RESULTS_VISIBILITIES = ['live', 'after_close', 'restricted'] as const;
export const PollResultsVisibilitySchema = z.enum(POLL_RESULTS_VISIBILITIES);
export type PollResultsVisibility = z.infer<typeof PollResultsVisibilitySchema>;

export const POLL_FIELD_TYPES = [
  'single_choice',
  'multi_choice',
  'dropdown',
  'short_text',
  'long_text',
  'scale',
  'number',
  'number_range',
  'date',
  'date_range',
  'time',
  'datetime',
  'section',
  'file',
  'content',
  'image',
] as const;
export const PollFieldTypeSchema = z.enum(POLL_FIELD_TYPES);
export type PollFieldType = z.infer<typeof PollFieldTypeSchema>;

/** Layout elements: shown to respondents but never answered or aggregated. */
const DISPLAY_FIELD_TYPES: ReadonlySet<PollFieldType> = new Set(['section', 'content', 'image']);
export const isAnswerableFieldType = (type: PollFieldType): boolean =>
  !DISPLAY_FIELD_TYPES.has(type);

/** Readable share-link slug: `/to-polls/<slug>`. */
export const PollSlugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers, and single hyphens');

/** "Survey Example!" → "survey-example"; '' when nothing Latin/numeric remains (e.g. Thai titles). */
export const toPollSlug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');

export const POLL_IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const POLL_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const PollFieldConfigSchema = z
  .object({
    allowAddOption: z.boolean().optional(),
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().positive().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    minDate: z.string().date().optional(),
    maxDate: z.string().date().optional(),
    maxLength: z.number().int().positive().max(10_000).optional(),
    /** `content` element body (markdown). */
    markdown: z.string().max(10_000).optional(),
    /** `image` element storage key; the API signs it into `imageUrl` on read. */
    imagePath: z
      .string()
      .regex(/^polls\/[0-9a-f-]{36}\.(jpeg|png|webp)$/, 'Invalid image path')
      .optional(),
    /** Response-only signed URL for `imagePath`; ignored on write. */
    imageUrl: z.string().url().optional(),
  })
  .refine(
    (v) => !v.minDate || !v.maxDate || v.minDate <= v.maxDate,
    'minDate must be less than or equal to maxDate',
  )
  .refine(
    (v) => v.min === undefined || v.max === undefined || v.min <= v.max,
    'min must be less than or equal to max',
  )
  .refine(
    (v) =>
      v.minSelections === undefined ||
      v.maxSelections === undefined ||
      v.minSelections <= v.maxSelections,
    'minSelections must be less than or equal to maxSelections',
  );
export type PollFieldConfig = z.infer<typeof PollFieldConfigSchema>;

export const PollFieldOptionInputSchema = z.object({
  id: z.string().uuid().optional(),
  label: z.string().trim().min(1).max(200),
  jumpToSectionId: z
    .union([z.string().uuid(), z.literal('submit')])
    .nullable()
    .default(null),
});
export type PollFieldOptionInput = z.input<typeof PollFieldOptionInputSchema>;

export const PollFieldInputSchema = z.object({
  id: z.string().uuid().optional(),
  type: PollFieldTypeSchema,
  label: z.string().trim().min(1).max(500),
  helpText: z.string().trim().max(2_000).default(''),
  required: z.boolean().default(false),
  config: PollFieldConfigSchema.default({}),
  options: z.array(PollFieldOptionInputSchema).max(200).default([]),
});
export type PollFieldInput = z.input<typeof PollFieldInputSchema>;

export const PollFieldOptionSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  jumpToSectionId: z.union([z.string().uuid(), z.literal('submit')]).nullable(),
  addedBy: UserRefSchema.nullable(),
});
export type PollFieldOption = z.infer<typeof PollFieldOptionSchema>;

export const PollFieldSchema = z.object({
  id: z.string().uuid(),
  type: PollFieldTypeSchema,
  label: z.string(),
  helpText: z.string(),
  required: z.boolean(),
  config: PollFieldConfigSchema,
  options: z.array(PollFieldOptionSchema),
});
export type PollField = z.infer<typeof PollFieldSchema>;

const AnswerRangeSchema = z.object({
  start: z.union([z.string(), z.number()]),
  end: z.union([z.string(), z.number()]),
});
export const PollAnswerValueSchema = z.union([
  z.string(),
  z.number().finite(),
  z.array(z.string()),
  AnswerRangeSchema,
]);
export type PollAnswerValue = z.infer<typeof PollAnswerValueSchema>;

export const PollAnswerSchema = z.object({
  fieldId: z.string().uuid(),
  value: PollAnswerValueSchema,
});
export type PollAnswer = z.infer<typeof PollAnswerSchema>;

export const PollResponseSchema = z.object({
  id: z.string().uuid(),
  submittedAt: z.string(),
  updatedAt: z.string(),
  answers: z.array(PollAnswerSchema),
});
export type PollResponse = z.infer<typeof PollResponseSchema>;

export const PollListItemSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  status: PollStatusSchema,
  isAnonymous: z.boolean(),
  allowEdit: z.boolean(),
  resultsVisibility: PollResultsVisibilitySchema,
  closesAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdBy: UserRefSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  responseCount: z.number().int().nonnegative(),
  hasResponded: z.boolean(),
  canViewResults: z.boolean(),
  canManage: z.boolean(),
});
export type PollListItem = z.infer<typeof PollListItemSchema>;

export const PollDetailSchema = PollListItemSchema.extend({
  fields: z.array(PollFieldSchema),
  myResponse: PollResponseSchema.nullable(),
});
export type PollDetail = z.infer<typeof PollDetailSchema>;

export const PollFieldResultSchema = z.object({
  fieldId: z.string().uuid(),
  label: z.string(),
  type: PollFieldTypeSchema,
  responseCount: z.number().int().nonnegative(),
  options: z.array(
    z.object({ id: z.string().uuid(), label: z.string(), count: z.number().int().nonnegative() }),
  ),
  average: z.number().nullable(),
  distribution: z.array(z.object({ value: z.string(), count: z.number().int().nonnegative() })),
  values: z.array(PollAnswerValueSchema),
});
export type PollFieldResult = z.infer<typeof PollFieldResultSchema>;

export const PollResultsSchema = z.object({
  poll: PollDetailSchema.omit({ myResponse: true, fields: true }),
  fields: z.array(PollFieldResultSchema),
  responseCount: z.number().int().nonnegative(),
  respondents: z
    .array(
      z.object({
        user: UserRefSchema,
        submittedAt: z.string(),
        answers: z.array(PollAnswerSchema),
      }),
    )
    .nullable(),
});
export type PollResults = z.infer<typeof PollResultsSchema>;
